/**
 * One locale for every number on screen.
 *
 * The page said `$1209.49` beside `3.484 findings` and `58.161 output tokens`:
 * `toFixed` groups nothing and a bare `toLocaleString()` groups in the
 * *browser's* locale, so on a German machine an English page wrote thousands
 * with a full stop (#124). Both go through here, in en-US, because that is the
 * language the interface is written in.
 *
 * Dates too. They still followed the browser, so the same screen read
 * `$1,209.49` and `12. Okt., 08:45` — two locales a line apart. The clock is
 * 24-hour, which is what the people reading it use.
 */
const LOCALE = "en-US";
const GROUPED = new Intl.NumberFormat(LOCALE);
const CENTS = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function count(value: number | undefined | null): string {
  return GROUPED.format(value ?? 0);
}

export function money(value: number | undefined | null): string {
  const amount = value ?? 0;
  if (amount === 0) return "$0.00";
  if (amount > 0 && amount < 0.01) return "<$0.01";
  return amount < 0 ? `−$${CENTS.format(-amount)}` : `$${CENTS.format(amount)}`;
}

/** A price in the currency it is sold in — a credit package may be in euros. */
export function priced(amount: number, currency: string): string {
  return amount.toLocaleString(LOCALE, { style: "currency", currency: currency.toUpperCase() });
}

export function compactNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

export function duration(seconds: number | undefined | null): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${total % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86_400);
  if (days < 30) return `${days}d ago`;
  return date(iso);
}

/** `Oct 6, 2026`. */
export function date(iso: string | Date): string {
  return new Date(iso).toLocaleDateString(LOCALE, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** `Oct 06, 08:45`, or with seconds — a log row's stamp. */
export function stamp(when: Date, seconds = false): string {
  return when.toLocaleString(LOCALE, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    hourCycle: "h23",
  });
}

/** The whole instant, for a tooltip. */
export function fullStamp(when: Date): string {
  return when.toLocaleString(LOCALE, { dateStyle: "medium", timeStyle: "medium", hourCycle: "h23" });
}

export function clockTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const when = new Date(iso);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleTimeString(LOCALE, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      });
}

/** `business_plan` -> `Business plan`. Workstream keys are snake_case. */
export function humanise(key: string): string {
  const spaced = key.replace(/[_-]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function bytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} kB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
